/**
 * Your clues, in order — the one place where the whole puzzle is visible and can
 * be reordered, edited or trimmed. Still a single task, so it keeps the
 * one-thing-per-screen rule.
 *
 * Reordering works by dragging (pointer and keyboard, via dnd-kit) and by the
 * move up / move down buttons, which are the accessible path and the one that
 * works reliably on every mobile browser.
 */
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../chassis/Icon";
import { ClueMeter, Heading, PrimaryButton, StepChip, Sub } from "../chassis/widgets";
import { meterProps } from "./ClueScreens";
import { C, FONT, SHADOW, TINT } from "../chassis/tokens";
import { MAX_CLUES } from "../constants";
import { completeClues, isClueComplete } from "../state";

function CelebrationTag({ type, tr }) {
  if (!type || type === "default") {
    return (
      <span style={tagStyle}>
        <Icon name="sparkle" size={10} color={C.accentDeep} fill={C.accentDeep} />
        {tr("app.creator.celebration.options.default.title")}
      </span>
    );
  }
  const icon = { message: "message", photo: "image", video: "video", song: "music" }[type] || "sparkle";
  return (
    <span style={tagStyle}>
      <Icon name={icon} size={10} color={C.accentDeep} strokeWidth={2.6} />
      {tr(`app.creator.celebration.options.${type}.title`)}
    </span>
  );
}

const tagStyle = {
  display: "inline-flex", alignItems: "center", gap: 4, height: 22, padding: "0 8px", borderRadius: 999,
  background: C.accentSoft, fontSize: 11, fontWeight: 700, color: C.accentDeep,
};

// The opened row: everything this clue holds, read-only, with Edit as the
// explicit way in. Tapping a row used to jump straight into the question
// screen, which read as "write it again" when the user only wanted to look.
function ClueDetails({ clue, index, onEdit, tr }) {
  const line = (label, value) => (
    <div style={{ marginTop: 8 }}>
      <span style={{ display: "block", fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C.label }}>{label}</span>
      <span style={{ display: "block", marginTop: 2, fontSize: 14, fontWeight: 600, color: C.ink, overflowWrap: "anywhere" }}>{value}</span>
    </div>
  );
  const type = clue.celebrationType || "default";
  const celebration = tr(`app.creator.celebration.options.${type}.title`);
  const celebrationText = type === "message" && clue.celebrationContent ? `${celebration}: “${clue.celebrationContent}”` : celebration;
  return (
    <div data-testid={`creator-clue-details-${index}`} style={{ padding: "0 14px 14px 46px", marginTop: -6 }}>
      {clue.answer ? line(tr("app.creator.list.answer"), clue.answer) : null}
      {clue.hint ? line(tr("app.creator.list.hintTag"), clue.hint) : null}
      {line(tr("app.creator.list.celebration"), celebrationText)}
      <button
        type="button"
        data-testid={`creator-clue-edit-${index}`}
        onClick={onEdit}
        style={{
          marginTop: 12, height: 40, padding: "0 16px", borderRadius: 999, border: "none", background: C.accentSoft,
          display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.accentDeep, cursor: "pointer",
        }}
      >
        <Icon name="pencil" size={13} color={C.accentDeep} strokeWidth={2.4} />
        {tr("app.creator.list.edit")}
      </button>
    </div>
  );
}

function ClueRow({ clue, index, total, open, onToggle, onEdit, onDelete, onMove }) {
  const { t: tr } = useTranslation();
  const id = `clue-${index}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const incomplete = !isClueComplete(clue);

  return (
    <div
      ref={setNodeRef}
      data-testid={`creator-clue-row-${index}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        borderRadius: 20, background: C.surface, fontFamily: FONT,
        border: incomplete ? `1.5px dashed ${C.dash}` : "none",
        boxShadow: isDragging
          ? `0 0 0 4px ${TINT.accent(0.10)}, 0 26px 44px -18px ${TINT.accent(0.55)}`
          : SHADOW.card,
        zIndex: isDragging ? 2 : 1,
        position: "relative",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 10px 12px 8px" }}>
      <button
        type="button"
        aria-label={tr("app.creator.list.drag", { n: index + 1 })}
        style={{ flexShrink: 0, width: 28, height: 44, border: "none", background: "transparent", display: "grid", placeItems: "center", cursor: "grab", touchAction: "none" }}
        {...attributes}
        {...listeners}
      >
        <Icon name="drag" size={18} color={isDragging ? C.accent : C.label} strokeWidth={3} />
      </button>

      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`creator-clue-open-${index}`}
        style={{ flexGrow: 1, minWidth: 0, border: "none", background: "transparent", textAlign: "left", padding: 0, cursor: "pointer", fontFamily: FONT }}
      >
        {/* Always "Clue N" first: the optional name follows it, so a one-letter
            name can't pass for the clue's label. */}
        <span style={{ display: "block", fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C.label, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {tr("app.creator.clue.number", { n: index + 1 })}
          {clue.title ? <span style={{ color: C.accent }}> · {clue.title}</span> : null}
        </span>
        <span style={{ display: "block", marginTop: 3, fontSize: 15, fontWeight: 800, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: open ? "normal" : "nowrap" }}>
          {clue.question || tr("app.creator.list.unfinished")}
        </span>
        {open ? null : <span style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 7, flexWrap: "wrap" }}>
          {clue.answer ? (
            <span style={{ fontSize: 12, fontWeight: 600, color: C.soft }}>
              {tr("app.creator.list.answer")} · <strong style={{ color: C.ink }}>{clue.answer}</strong>
            </span>
          ) : null}
          <CelebrationTag type={clue.celebrationType} tr={tr} />
          {clue.hint ? <span style={{ ...tagStyle, background: C.surfaceAlt, color: C.muted }}>{tr("app.creator.list.hintTag")}</span> : null}
        </span>}
      </button>

      <span style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
        <button type="button" aria-label={tr("app.creator.list.moveUp", { n: index + 1 })} disabled={index === 0} onClick={() => onMove(index, index - 1)} style={miniBtn(index === 0)}>
          <Icon name="up" size={13} color={index === 0 ? C.disabled : C.body} strokeWidth={2.6} />
        </button>
        <button type="button" aria-label={tr("app.creator.list.moveDown", { n: index + 1 })} disabled={index === total - 1} onClick={() => onMove(index, index + 1)} style={miniBtn(index === total - 1)}>
          <Icon name="down" size={13} color={index === total - 1 ? C.disabled : C.body} strokeWidth={2.6} />
        </button>
      </span>

      <button
        type="button"
        aria-label={tr("app.creator.list.delete", { n: index + 1 })}
        data-testid={`creator-clue-delete-${index}`}
        onClick={onDelete}
        style={{ flexShrink: 0, width: 44, height: 44, borderRadius: 14, border: "none", background: C.surfaceAlt, display: "grid", placeItems: "center", cursor: "pointer" }}
      >
        <Icon name="trash" size={16} color={C.label} strokeWidth={2.1} />
      </button>
      </div>
      {open ? <ClueDetails clue={clue} index={index} onEdit={onEdit} tr={tr} /> : null}
    </div>
  );
}

const miniBtn = (disabled) => ({
  width: 26, height: 21, borderRadius: 7, border: "none", background: "transparent",
  display: "grid", placeItems: "center", cursor: disabled ? "default" : "pointer", padding: 0,
});

export function ClueListScreen({ state, dispatch }) {
  const { t: tr } = useTranslation();
  const written = completeClues(state).length;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const ids = state.clues.map((_, i) => `clue-${i}`);
  const [openIndex, setOpenIndex] = useState(null);

  function onDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOpenIndex(null);
    dispatch({ type: "moveClue", from: ids.indexOf(active.id), to: ids.indexOf(over.id) });
  }

  return (
    <>
      <div style={{ padding: "0 4px" }}>
        <StepChip badge={written} label={tr("app.creator.list.eyebrow")} />
        <Heading size={32} accent={tr("app.creator.list.headingAccent")}>{tr("app.creator.list.heading")}</Heading>
        <Sub>{tr("app.creator.list.sub", { name: state.recipientName })}</Sub>
        <ClueMeter {...meterProps(written, tr)} />
      </div>

      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10, paddingBottom: 8 }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            {state.clues.map((clue, i) => (
              <ClueRow
                key={ids[i]}
                clue={clue}
                index={i}
                total={state.clues.length}
                open={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                onEdit={() => dispatch({ type: "editClue", index: i })}
                onDelete={() => { setOpenIndex(null); dispatch({ type: "deleteClue", index: i }); }}
                onMove={(from, to) => { setOpenIndex(null); dispatch({ type: "moveClue", from, to }); }}
              />
            ))}
          </SortableContext>
        </DndContext>

        {written < MAX_CLUES && (
          <button
            type="button"
            data-testid="creator-list-add"
            onClick={() => dispatch({ type: "next", intent: "add" })}
            style={{
              height: 56, borderRadius: 20, border: `1.5px dashed ${C.dash}`, background: TINT.glass(0.55),
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              fontFamily: FONT, fontSize: 14, fontWeight: 700, color: C.accentDeep, cursor: "pointer",
            }}
          >
            <Icon name="plus" size={15} color={C.accentDeep} strokeWidth={2.5} />
            {tr("app.creator.list.add")}
            <span style={{ fontSize: 12, fontWeight: 600, color: C.label }}>{tr("app.creator.list.left", { count: MAX_CLUES - written })}</span>
          </button>
        )}
      </div>
    </>
  );
}

export function listFooter({ state, tr, dispatch }) {
  const written = completeClues(state).length;
  const enough = written >= 2;
  return (
    <PrimaryButton
      testId="creator-next"
      onClick={() => dispatch({ type: "next", intent: enough ? undefined : "add" })}
    >
      {enough ? tr("app.creator.list.cta") : tr("app.creator.reward.writeNext", { n: written + 1 })}
    </PrimaryButton>
  );
}
